/**
 * CommandPalette — generic Linear-style Cmd+K palette primitive.
 *
 * Wraps the `cmdk` library inside the shared Radix Dialog primitive so it
 * picks up the same portal / overlay tokens as every other modal in the
 * app. The component is **purely presentational** — group + item state is
 * passed in as props so any feature can compose its own lanes (documents,
 * conversations, projects, actions, …) without forking the palette shell.
 *
 * Why a primitive here instead of the v1 ConversationCommandPalette in
 * apps/client? The original lane (conversations + projects + chat actions)
 * is fine, but the Phase 3.2 work (Editor-first Canvas) needs to add a
 * Documents lane that lives on its own data slice. Building one shell that
 * accepts ``groups`` as a prop avoids a copy-paste palette per surface
 * (DRY rule from CLAUDE.md — "Sempre riusare ciò che già esiste").
 *
 * Wire shape (consumer):
 *
 *   <CommandPalette
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     placeholder="Cerca documenti, chat, azioni…"
 *     query={query}
 *     onQueryChange={setQuery}
 *     groups={[
 *       { id: "actions",      heading: "Azioni",        items: [...] },
 *       { id: "documents",    heading: "Documenti",     items: [...] },
 *       { id: "conversations",heading: "Chat recenti",  items: [...] },
 *     ]}
 *   />
 *
 * Keyboard contract (cmdk-native):
 *   - ↑ / ↓     navigate selectable items
 *   - Enter     activates the selected item's ``onSelect``
 *   - ⌘+Enter   activates the selected item's ``onAltSelect`` (if present)
 *   - Esc       closes the palette (Radix Dialog default)
 *
 * Brand polish: cream/orange selection, hairline border, ⌘K hint pill in
 * the input footer, framer-motion fade+scale entry (180ms ease-out). Lucide
 * 1.75 stroke. See [feedback_zustand_useshallow_for_derived_selectors.md]
 * — when a consumer derives ``groups`` from a Zustand selector, ensure the
 * selector is wrapped in ``useShallow`` to avoid React #185.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Command } from "cmdk";
import { Search } from "lucide-react";
import type { ComponentType, JSX, KeyboardEvent, ReactNode } from "react";
import { useEffect } from "react";

import { cn } from "../lib/cn";
import { Dialog, DialogContent } from "../primitives/dialog";

// ─── Public item / group shape ─────────────────────────────────────────────

/**
 * Single selectable row inside a palette group. ``onSelect`` is the
 * primary action (Enter). ``onAltSelect`` is an optional secondary action
 * triggered by ⌘+Enter — used in the documents lane to prefill the chat
 * composer with a modify-this-doc draft instead of just opening it.
 */
export interface CommandPaletteItem {
  /**
   * Stable unique id (collision-safe across groups — prefix with group
   * id in the consumer to be safe). Used as the cmdk ``value`` so the
   * built-in fuzzy filter has something deterministic to match against.
   */
  readonly id: string;
  /** Visible label (truncated to 1 line in the row). */
  readonly label: string;
  /**
   * Optional second-line caption (mime / relative time / sub-status).
   * Kept short — rendered at ``text-[11px] text-ink-3`` next to label.
   */
  readonly hint?: string;
  /** Optional trailing badge (e.g. ``"Attiva"`` for current conv). */
  readonly trailing?: ReactNode;
  /**
   * Optional Lucide icon component. Rendered at 14px, ink-3 by default;
   * the consumer can recolor via the wrapper if needed. We accept the
   * component (not a node) so the palette can apply uniform sizing.
   */
  readonly icon?: ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
  /**
   * Extra cmdk ``keywords`` for matching beyond ``label`` — e.g. the
   * filename ext, alternate names. Lowercased before matching.
   */
  readonly keywords?: ReadonlyArray<string>;
  /** Primary action (Enter). The palette closes automatically after. */
  readonly onSelect: () => void;
  /**
   * Optional secondary action (⌘+Enter). Used by the documents lane
   * to prefill the chat composer instead of opening the editor. When
   * unset, ⌘+Enter falls through to ``onSelect`` so the binding never
   * looks broken on rows that don't define it.
   */
  readonly onAltSelect?: () => void;
}

export interface CommandPaletteGroup {
  /** Stable id — referenced in keys + telemetry. */
  readonly id: string;
  /** Visible group header (uppercase, font-display semibold 10px). */
  readonly heading: string;
  /** Rows in this group. Empty arrays are filtered out automatically. */
  readonly items: ReadonlyArray<CommandPaletteItem>;
}

// ─── Component props ────────────────────────────────────────────────────────

export interface CommandPaletteProps {
  /** Controlled open state (singleton store in the consumer). */
  readonly isOpen: boolean;
  /** Fires on every dismissal (overlay click, Esc, item activation). */
  readonly onClose: () => void;
  /** Search input value — controlled by the consumer. */
  readonly query: string;
  readonly onQueryChange: (next: string) => void;
  /** Input placeholder (Italian by default in consumers). */
  readonly placeholder?: string;
  /** Italian empty-state copy. Default: ``"Nessun risultato"``. */
  readonly emptyMessage?: string;
  /**
   * Lanes to render. Items inside each group are filtered/scored by
   * ``cmdk`` against ``query``; empty groups are skipped automatically
   * so the palette never shows a header above zero rows.
   */
  readonly groups: ReadonlyArray<CommandPaletteGroup>;
  /**
   * Optional aria-label override for the dialog. Default
   * ``"Comandi rapidi"`` — matches the existing v1 palette wording.
   */
  readonly ariaLabel?: string;
  /**
   * Optional footer node — rendered below the results list. Used by
   * the conversation palette to show a ⌘+Enter hint when the focused
   * row supports an alt action.
   */
  readonly footer?: ReactNode;
}

// ─── Implementation ─────────────────────────────────────────────────────────

/**
 * Generic Cmd+K palette. The consumer wires the open state + groups +
 * footer; the component owns input rendering, cmdk scoring, keyboard
 * routing and brand polish.
 */
export function CommandPalette({
  isOpen,
  onClose,
  query,
  onQueryChange,
  placeholder,
  emptyMessage,
  groups,
  ariaLabel,
  footer,
}: CommandPaletteProps): JSX.Element {
  const { i18n } = useLingui();
  const resolvedPlaceholder = placeholder ?? i18n._(msg`Cerca…`);
  const resolvedEmptyMessage = emptyMessage ?? i18n._(msg`Nessun risultato`);
  const resolvedAriaLabel = ariaLabel ?? i18n._(msg`Comandi rapidi`);
  // Reset the query whenever the palette closes so the next open starts
  // fresh — matches Linear / Notion behaviour. We do this on the open
  // *transition*, not on every render, to avoid clobbering a programmatic
  // query update mid-session.
  useEffect(() => {
    if (!isOpen) onQueryChange("");
  }, [isOpen, onQueryChange]);

  // ⌘+Enter handling — cmdk doesn't surface alt-select natively, so we
  // intercept the keydown at the Command root, look up the focused item
  // via the ``cmdk-item[data-selected=true]`` selector and invoke its
  // ``onAltSelect`` callback. We delegate to ``onSelect`` (the default
  // Enter handler will fire too) when no alt is defined — never leaves
  // the user with a no-op key. Esc / Enter without modifier are owned by
  // cmdk + Radix; we only step in for the ⌘/Ctrl+Enter shortcut.
  const onCommandKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "Enter") return;
    if (!(e.metaKey || e.ctrlKey)) return;
    const selected = e.currentTarget.querySelector<HTMLElement>(
      '[cmdk-item][data-selected="true"]',
    );
    if (!selected) return;
    const itemId = selected.getAttribute("data-item-id");
    if (!itemId) return;
    // Flat lookup across every group — ids are caller-stable. We don't
    // ``memo`` this map because the groups change on every keystroke;
    // building the lookup costs a single O(n) walk per Enter event.
    for (const g of groups) {
      const found = g.items.find((it) => it.id === itemId);
      if (found) {
        e.preventDefault();
        e.stopPropagation();
        const handler = found.onAltSelect ?? found.onSelect;
        handler();
        onClose();
        return;
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className={cn("max-w-[560px] overflow-hidden p-0", "border-hairline bg-card")}
        ariaTitle={resolvedAriaLabel}
      >
        <Command
          label={resolvedAriaLabel}
          shouldFilter={true}
          onKeyDown={onCommandKeyDown}
          className={cn(
            "flex flex-col overflow-hidden",
            // Input row — hairline divider, 11pt search font, ink-3 placeholder.
            "[&_[cmdk-input-wrapper]]:flex [&_[cmdk-input-wrapper]]:items-center [&_[cmdk-input-wrapper]]:border-hairline [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:px-3",
            "[&_[cmdk-input]]:h-11 [&_[cmdk-input]]:flex-1 [&_[cmdk-input]]:bg-transparent [&_[cmdk-input]]:text-[13px] [&_[cmdk-input]]:text-ink-1 [&_[cmdk-input]]:outline-none [&_[cmdk-input]]:placeholder:text-ink-3",
            // List — capped at 420px, hairline scrollbar.
            "[&_[cmdk-list]]:max-h-[420px] [&_[cmdk-list]]:overflow-y-auto [&_[cmdk-list]]:p-1.5",
            // Empty / loading messages.
            "[&_[cmdk-empty]]:py-6 [&_[cmdk-empty]]:text-center [&_[cmdk-empty]]:text-[12px] [&_[cmdk-empty]]:text-ink-3",
            // Group headers — font-display semibold, 10px, uppercase
            // tracking-wider so the lanes read as scannable sections.
            "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:font-display [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:text-ink-3 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
            // Item rows — 13px ink-1, 8px gap, soft orange selection.
            "[&_[cmdk-item]]:flex [&_[cmdk-item]]:cursor-pointer [&_[cmdk-item]]:items-center [&_[cmdk-item]]:gap-2 [&_[cmdk-item]]:rounded-md [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]]:text-[13px] [&_[cmdk-item]]:text-ink-1",
            "[&_[cmdk-item][data-selected=true]]:bg-ando-orange-soft [&_[cmdk-item][data-selected=true]]:text-ink-1",
          )}
        >
          {/* Search input — autoFocus is owned by cmdk itself. */}
          <div cmdk-input-wrapper="">
            <Search className="h-3.5 w-3.5 text-ink-3" size={14} strokeWidth={1.75} aria-hidden />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={onQueryChange}
              placeholder={resolvedPlaceholder}
              aria-label={resolvedPlaceholder}
            />
            <span
              className="ml-2 hidden select-none rounded border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-ink-3 sm:inline"
              aria-hidden
            >
              esc
            </span>
          </div>

          <Command.List>
            <Command.Empty>{resolvedEmptyMessage}</Command.Empty>

            {groups.map((group) => {
              if (group.items.length === 0) return null;
              return (
                <Command.Group key={group.id} heading={group.heading}>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Command.Item
                        key={item.id}
                        // ``data-item-id`` is consumed by the ⌘+Enter
                        // lookup above. The ``value`` is what cmdk
                        // scores against — we include the label +
                        // hint + keywords so the user can search by
                        // any token (filename / mime / "Crea").
                        value={[item.id, item.label, item.hint ?? "", ...(item.keywords ?? [])]
                          .join(" ")
                          .toLowerCase()}
                        data-item-id={item.id}
                        onSelect={() => {
                          item.onSelect();
                          onClose();
                        }}
                      >
                        {Icon ? (
                          <Icon
                            className="h-3.5 w-3.5 flex-shrink-0 text-ink-3"
                            size={14}
                            strokeWidth={1.75}
                          />
                        ) : null}
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.hint ? (
                          <span className="text-[11px] text-ink-3">{item.hint}</span>
                        ) : null}
                        {item.trailing}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              );
            })}
          </Command.List>

          {footer ? (
            <div className="border-hairline border-t px-3 py-2 text-[11px] text-ink-3">
              {footer}
            </div>
          ) : null}
        </Command>
      </DialogContent>
    </Dialog>
  );
}
